import { ChevronDown, ChevronRight, SlidersHorizontal } from "lucide-react"
import { Textarea } from "@/components/ui/primitives"
import { cn } from "@/lib/utils"
import { useTranslation } from "@/hooks/use-translation"
import type { StoryboardAnimaticRefFrame } from "@/lib/inertia-actions"

export type ShotStoryboardGenerationExtrasProps = {
  storyboardPreviewUrl: string | null
  sendCurrentAsRef: boolean
  onSendCurrentAsRefChange: (value: boolean) => void
  hasAnimaticSegment: boolean
  animaticRefFrame: StoryboardAnimaticRefFrame | null
  onAnimaticRefFrameChange: (value: StoryboardAnimaticRefFrame | null) => void
  animaticFrames: Array<{
    key: StoryboardAnimaticRefFrame
    label: string
    url?: string | null
    ok: boolean
  }>
  includeLocation: boolean
  includeCharacters: boolean
  includeProps: boolean
  locationBlock: string | null
  charactersBlock: string | null
  propsBlock: string | null
  onToggleBlock: (block: string | null, include: boolean, kind: "location" | "characters" | "props") => void
  providerLabel: string
  systemOpen: boolean
  onSystemOpenChange: (open: boolean) => void
  systemPrompt: string
  onSystemPromptChange: (value: string) => void
}

export function ShotStoryboardGenerationExtras({
  storyboardPreviewUrl,
  sendCurrentAsRef,
  onSendCurrentAsRefChange,
  hasAnimaticSegment,
  animaticRefFrame,
  onAnimaticRefFrameChange,
  animaticFrames,
  includeLocation,
  includeCharacters,
  includeProps,
  locationBlock,
  charactersBlock,
  propsBlock,
  onToggleBlock,
  providerLabel,
  systemOpen,
  onSystemOpenChange,
  systemPrompt,
  onSystemPromptChange,
}: ShotStoryboardGenerationExtrasProps) {
  const { t } = useTranslation(["shot", "common"])

  return (
    <div>
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase">
        <SlidersHorizontal className="h-3 w-3" /> {t("common.generationSettings")}
      </div>
      <div className="space-y-3">
        {locationBlock || charactersBlock || propsBlock ? (
          <div className="flex flex-wrap gap-x-5 gap-y-1.5">
            {locationBlock ? (
              <label className="flex cursor-pointer items-center gap-2 text-xs text-ui-text-secondary">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-ui-border-strong bg-ui-surface-sunken"
                  checked={includeLocation}
                  onChange={(e) => onToggleBlock(locationBlock, e.target.checked, "location")}
                />
                {t("common.addLocationDescription")}
              </label>
            ) : null}
            {charactersBlock ? (
              <label className="flex cursor-pointer items-center gap-2 text-xs text-ui-text-secondary">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-ui-border-strong bg-ui-surface-sunken"
                  checked={includeCharacters}
                  onChange={(e) => onToggleBlock(charactersBlock, e.target.checked, "characters")}
                />
                {t("common.addHeroDescription")}
              </label>
            ) : null}
            {propsBlock ? (
              <label className="flex cursor-pointer items-center gap-2 text-xs text-ui-text-secondary">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-ui-border-strong bg-ui-surface-sunken"
                  checked={includeProps}
                  onChange={(e) => onToggleBlock(propsBlock, e.target.checked, "props")}
                />
                {t("common.addPropsDescription")}
              </label>
            ) : null}
          </div>
        ) : null}

        <div className="space-y-2 rounded-lg border border-ui-border bg-ui-surface px-3 py-2.5">
          <p className="text-[10px] font-medium tracking-wide text-ui-text-muted uppercase">
            {t("common.extraReferences")}
          </p>
          <label
            className={cn(
              "flex cursor-pointer items-start gap-2 text-xs text-ui-text-secondary",
              !storyboardPreviewUrl && "cursor-not-allowed opacity-50",
            )}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-ui-border-strong bg-ui-surface-sunken"
              checked={sendCurrentAsRef}
              disabled={!storyboardPreviewUrl}
              onChange={(e) => onSendCurrentAsRefChange(e.target.checked)}
            />
            <span>
              <span className="font-medium text-ui-text-primary">{t("common.sendCurrentAsReference")}</span>
              <span className="mt-0.5 block text-[10px] leading-snug text-ui-text-muted">
                {t("common.sendCurrentAsReferenceHint")}
              </span>
            </span>
          </label>
          <label
            className={cn(
              "flex cursor-pointer items-start gap-2 text-xs text-ui-text-secondary",
              !hasAnimaticSegment && "cursor-not-allowed opacity-50",
            )}
          >
            <input
              type="checkbox"
              className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-ui-border-strong bg-ui-surface-sunken"
              checked={animaticRefFrame != null}
              disabled={!hasAnimaticSegment}
              onChange={(e) => {
                if (e.target.checked) onAnimaticRefFrameChange("first")
                else onAnimaticRefFrameChange(null)
              }}
            />
            <span>
              <span className="font-medium text-ui-text-primary">{t("common.addAnimaticFrameReference")}</span>
              <span className="mt-0.5 block text-[10px] leading-snug text-ui-text-muted">
                {t("common.addAnimaticFrameReferenceHint")}
              </span>
            </span>
          </label>
          {animaticRefFrame != null && hasAnimaticSegment ? (
            <div className="flex flex-wrap items-end gap-3 pt-1 pl-1">
              {animaticFrames.map(({ key, label, url, ok }) => (
                <div key={key} className="flex flex-col gap-1">
                  <button
                    type="button"
                    disabled={!ok || !url}
                    title={!ok || !url ? t("common.frameNotReady") : label}
                    onClick={() => onAnimaticRefFrameChange(key)}
                    className={cn(
                      "relative h-14 w-14 overflow-hidden rounded-lg border-2 transition-colors",
                      animaticRefFrame === key
                        ? "border-ui-accent ring-1 ring-[var(--ui-focus-ring)]/40 ring-inset"
                        : "border-ui-border hover:border-white/25",
                      (!ok || !url) && "cursor-not-allowed opacity-40",
                    )}
                  >
                    {url ? (
                      <img src={url} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[9px] text-ui-text-secondary dark:bg-ui-bg-elevated light:bg-clay-2">
                        …
                      </div>
                    )}
                  </button>
                  <span className="max-w-[3.5rem] text-center text-[9px] text-ui-text-muted">{label}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>

        <div className="flex items-center gap-2 rounded-lg border border-white/5 bg-ui-surface px-3 py-2 text-[11px] text-ui-text-muted">
          <span className="font-medium text-ui-text-secondary">{t("common.provider")}</span> {providerLabel}
        </div>

        <div>
          <button
            type="button"
            onClick={() => onSystemOpenChange(!systemOpen)}
            className="flex items-center gap-1 text-[11px] font-semibold tracking-wide text-ui-text-muted uppercase hover:text-ui-text-secondary"
          >
            {systemOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            {t("common.systemPrompt")}
          </button>
          {systemOpen ? (
            <Textarea
              value={systemPrompt}
              onChange={(e) => onSystemPromptChange(e.target.value)}
              className="mt-1.5 min-h-[120px] font-mono text-xs leading-relaxed"
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
